import { animate, AnimationEvent, keyframes, state, style, transition, trigger } from '@angular/animations';
import { ConnectedPosition, FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  DOCUMENT
} from '@angular/core';
import { Subject, asyncScheduler, fromEvent, merge, Subscription } from 'rxjs';
import { subscribeOn, takeUntil, tap, throttleTime, delay } from 'rxjs/operators';
import { ControlValueAccessor } from '@angular/forms';
import { getFocusableElements, isElementFocusable, provideValueAccessor, TemplateUuids } from 'src/app/utils/shared-utils';
import { DropdownOption } from './dropdown-option';
import { MheOption } from '../shared';
import { ButtonPurpose } from '../button/button-purpose';
import { TranslateService } from '../../services/translate';
import { DropdownService } from './dropdown.service';
import dropdownPositions from './dropdown-positions';

import { differenceWith, isEqual, uniqBy } from 'lodash-es';
import { HTMLElementEvent } from 'src/app/utils/html-dom-events';
import { CdkPortal, TemplatePortal } from '@angular/cdk/portal';

const DEFAULT_MENU_WIDTH = 250;
const DROPDOWN_SELECTED_STATE_TIME = 250;

@Component({
    providers: [provideValueAccessor(DropdownComponent)],
    selector: 'app-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    animations: [
        trigger('optionSelection', [
            state('selected', style({
                backgroundColor: '#e0e7e9',
            })),
            transition('* => selected', [
                animate(`${DROPDOWN_SELECTED_STATE_TIME}ms`, keyframes([
                    style({ backgroundColor: 'white', offset: 0 }),
                    style({ backgroundColor: '#e0e7e9', offset: 1.0 }),
                ])),
            ]),
        ]),
        trigger('hideDropdown', [
            state('dropdownHidden', style({ opacity: 0 })),
            transition('* => dropdownHidden', animate(`${DROPDOWN_SELECTED_STATE_TIME}ms`)),
        ]),
    ],
    standalone: false
})
export class DropdownComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @Input() minWidth = 0;
  @Input() maxWidth = 250;
  @Input() splitActionText: string | null = null;
  @Input() disabled = false;
  @Input() image: string;
  @Input() optionPosition: 'left' | 'right' = 'left';
  @Input() toggleButtonAriaLabel = '';
  @Input() listboxLabel?: string;
  @Input() selected?: DropdownOption[] | MheOption[];
  @Input() isMenu?: boolean;
  @Input() icon = '';
  @Input() multiple = false;
  @Input() filterable = false;
  @Input() syncWidths = false;
  @Input() buttonWidth: number;
  @Input() searchable = false;
  @Input() selectAll = true;
  @Input() selectAllText = 'Select all';
  @Input() placeholder = 'Select';
  @Input() panelClass = '';
  @Input() closeOnBlur = false;
  @Input() updateDropdownLabel = true;
  @Input() showSelectedIcon = true;
  @Input() dropdownMenuCustomClass: string = ''
  @Input() previousFocusedElement: HTMLElement = null;
  @Input() automationIdPrefix: string = ''
  
  /** Flattened list of all options */
  _flatOptions: DropdownOption[] | MheOption[] = [];
  /** Filtered list of options to display */
  _filteredOptions: DropdownOption[] | MheOption[] = [];
  /** Tracks which options are nested */
  _isChild: boolean[];
  /** Search term for filtering options */
  _searchTerm = '';

  @Input()
  get options(): DropdownOption[] | MheOption[] {
    return this._filteredOptions;
  }
  set options(options: DropdownOption[] | MheOption[]) {
    this._isChild = [];

    this._flatOptions = (options ?? []).reduce<DropdownOption[] | MheOption[]>((acc, cur) => {
      this._isChild.push(false);

      const children = cur.children ?? [];
      for (const _c of children) {
        this._isChild.push(true);
      }

      return [...acc, cur, ...children];
    }, []);

    this._filteredOptions = this.filterOptions(this._searchTerm);

    this.checkSelectAll();
  }

  // value
  @Input()
  get value() {
    return this.selected ?? [];
  }
  set value(value: MheOption[]) {
    this.selected = value;
  }

  @Output() selectionChange: EventEmitter<any> = new EventEmitter();
  @Output() searchTerm: EventEmitter<string> = new EventEmitter();
  @Output() scrollBottomReached: EventEmitter<any> = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() close: EventEmitter<any> = new EventEmitter();
  @Output() splitActionClick: EventEmitter<any> = new EventEmitter();

  @ViewChild('controlButton') controlButton: ElementRef;
  @ViewChild('splitActionButton') splitActionButton: ElementRef;
  @ViewChild('toggleButtonWithIcon') toggleButtonWithIcon: ElementRef;
  @ViewChild('dropdownMenuPortal', { static: true }) dropdownMenuPortal: TemplateRef<any>;

  private readonly doc: Document;
  private readonly destroy$ = new Subject<void>();

  // expose import to template
  _buttonPurpose = ButtonPurpose;

  allSelected = false;
  focusedOption: any = null;
  isExpanded = false;
  listItems: Element[];
  overlayRef: OverlayRef;
  positionStrategy?: FlexibleConnectedPositionStrategy;
  searchIsFocused = false;

  private readonly FOOTER_HEIGHT = 57;
  private readonly SCROLL_THROTTLE_MS = 100;
  selectedSuffix: string | undefined = 'selected';
  templateUuids = new TemplateUuids();
  triggerHideDropdown = false;
  triggerOptionSelectionAnimation = false;
  openedEventSubscription: Subscription;
  focusedIndex: number;

  /** miscl. getters */
  get currentSelection(): string {
    return this.getDropdownDisplay(this.selected, this.placeholder, this.updateDropdownLabel);
  }

  // TODO: this smells like an anti-pattern
  get buttonId(): string {
    return this.templateUuids.get('buttonId');
  }

  get hasIcon() {
    return this.icon !== '';
  }

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private translateService: TranslateService,
    private overlay: Overlay,
    private dropdownService: DropdownService,
    private viewContainerRef: ViewContainerRef,
    @Inject(DOCUMENT) _doc: Document
  ) {
    this.doc = _doc;
  }

  ngOnInit() {
    this.translateService.translate('ngx-shared-selected').subscribe((value) => {
      this.selectedSuffix = value;
    });
    this.openedEventSubscription = this.dropdownService.opened.subscribe((dropdown) => {
      // If the opened event was sent by this instance, don't close the dropdown.
      if (dropdown !== this && this.isExpanded) {
        this.hideDropdown();
      }
    });
  }

  ngOnDestroy() {
    this.hideDropdown(true);
    this.destroy$.next();
    this.destroy$.unsubscribe();
    if (this.openedEventSubscription) {
      this.openedEventSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.options) {
      const options: DropdownOption[] | MheOption[] = changes.options.currentValue ?? [];

      if (uniqBy(options, 'value').length !== options.length) {
        throw new Error('options must have unique values');
      }
    }

    if (changes.selected) {
      const selected: DropdownOption[] | MheOption[] = changes.selected.currentValue ?? [];

      if (!this.multiple && selected.length > 1) {
        throw new Error('selected must contain only one element unless multiple is true');
      }

      this.checkSelectAll();
    }
  }

  createDropdownPortal() {
    const optionPosition = this.splitActionText ? 'right' : this.optionPosition;
    this.positionStrategy = this.getPositionStrategy(optionPosition);

    this.overlayRef = this.overlay.create({
      positionStrategy: this.positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      width: this.width(),
      panelClass: this.panelClass,
    });
  }

  /**
   * Dynamically updates the dropdown's max-height to prevent it from extending behind the fixed footer.
   * Called when CDK repositions the dropdown (via positionChanges) and during scroll events.
   * Calculates available space based on the overlay's actual position (above or below the button).
   */
  private updateDropdownHeight(): void {
    if (!this.overlayRef?.overlayElement) {
      return;
    }

    const footerTop = window.innerHeight - this.FOOTER_HEIGHT;
    const overlayRect = this.overlayRef.overlayElement.getBoundingClientRect();
    const buttonRect = this.dropdownButton.getBoundingClientRect();

    // Determine if dropdown is positioned above or below the button
    const isPositionedAbove = overlayRect.top < buttonRect.top;
    
    let availableHeight: number;
    if (isPositionedAbove) {
      // Space from viewport top to button top
      availableHeight = buttonRect.top;
    } else {
      // Space from overlay's actual position to footer top
      availableHeight = footerTop - overlayRect.top;
    }

    this.overlayRef.overlayElement.style.maxHeight = `${Math.max(0, availableHeight)}px`;
  }

  getPositionStrategy(optionPosition: 'left' | 'right') {
    const { bottom_left, bottom_right, top_left, top_right } = dropdownPositions;

    const positions: ConnectedPosition[] =
      optionPosition === 'right' ? [bottom_right, top_right] : [bottom_left, top_left];

    // Flexible position strategy bounds dropdown by window and allows to grow to maximize possible height
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.controlButton || this.splitActionButton || this.toggleButtonWithIcon)
      .withFlexibleDimensions(true)
      .withGrowAfterOpen(true)
      .withPush(false)
      .withPositions([...positions]);

    return positionStrategy;
  }

  showDropdown() {
    this.createDropdownPortal();

    if (this.dropdownMenuPortal) {
      // Create a TemplatePortal from your template reference
      const templatePortal = new TemplatePortal(this.dropdownMenuPortal, this.viewContainerRef);
      // Attach the TemplatePortal instead of the raw template
      this.overlayRef.attach(templatePortal);
      // this.overlayRef.attach(this.dropdownMenuPortal);
      this.listItems = Array.from(this.overlayRef.hostElement.querySelectorAll('li[role="presentation"]'));

      // Listen to position changes
      // Using delay(0) to ensure initial height calculation happens after CDK positioning
      this.positionStrategy?.positionChanges
        .pipe(
          delay(0), // wait for CDK positioning to complete
          tap(() => this.updateDropdownHeight()),
          takeUntil(this.overlayRef.detachments())
        )
        .subscribe();

      // Throttled scroll handler to update height constraint (prevents layout thrashing)
      // Listen to scroll events to dynamically update position and height
      fromEvent(window, 'scroll', { passive: true })
        .pipe(
          throttleTime(this.SCROLL_THROTTLE_MS, undefined, { leading: true, trailing: true }),
          tap(() => this.updateDropdownHeight()),
          takeUntil(this.overlayRef.detachments())
        )
        .subscribe();

      const events = [fromEvent(this.doc, 'click')];

      if (this.closeOnBlur) {
        events.push(fromEvent(window, 'blur'));
      }

      merge(...events)
        .pipe(
          tap((event: Event) => {
            this.onClickedOutside(event);
          }),
          subscribeOn(asyncScheduler),
          takeUntil(this.overlayRef.detachments())
        )
        .subscribe();
    }
  }

  hideDropdown(returnFocus = true) {
    this.triggerHideDropdown = false;

    if (this.overlayRef?.hasAttached()) {
      this.overlayRef.detach();
      this.overlayRef.dispose();

      if (returnFocus) {
        this.focusControlButton();
      }
    }

    this.isExpanded = false;
    this._changeDetectorRef.detectChanges();
    this.close.emit();

    if (this.isMenu) {
      this.resetSelectedState();
    }
  }

  resetSelectedState() {
    this.selected = [];
  }

  width() {
    const elementRef = this.controlButton || this.splitActionButton || this.toggleButtonWithIcon;
    const clientRect = elementRef.nativeElement.getBoundingClientRect();
    let width = clientRect.width;

    if (this.syncWidths) {
      return width;
    }

    if (width < DEFAULT_MENU_WIDTH) {
      width = 'auto';
    }

    if (width > DEFAULT_MENU_WIDTH) {
      width = DEFAULT_MENU_WIDTH;
    }

    return width;
  }

  getContext(i: any, option: any) {
    return { isChild: this._isChild[i], option };
  }

  containsOption(target: DropdownOption | MheOption, options: DropdownOption[] | MheOption[] | undefined) {
    return options?.some((option) => option.value === target.value);
  }

  selectOption(selectedOption: DropdownOption | MheOption) {
    if (selectedOption.disabled || !!selectedOption.children) {
      return;
    }

    if (!this.multiple) {
      this.selected = [selectedOption];
      this.triggerOptionSelectionAnimation = true;
    } else {
      if (this.containsOption(selectedOption, this.selected)) {
        this.selected = this.selected?.filter((option) => option.value !== selectedOption.value);
      } else {
        this.selected = (this.selected ?? []).concat(selectedOption);
      }
    }

    this.updateDropdown();
  }

  optionSelectionAnimationDone(event: AnimationEvent) {
    if (event.toState === 'selected') {
      this.triggerHideDropdown = true;
      this.triggerOptionSelectionAnimation = false;
    }
  }

  hideDropdownAnimationDone(event: any) {
    if (event.toState === 'dropdownHidden') {
      this.hideDropdown();
    }
  }

  selectAllOptions() {
    if (this.allSelected) {
      this.selected = [];
    } else {
      this.selected = [...this.options];
    }

    this.updateDropdown();
  }

  private checkSelectAll() {
    if (this.selected?.length && this.options.length === this.selected.length) {
      this.allSelected = !differenceWith(this.options, this.selected, isEqual).length;
    } else {
      this.allSelected = false;
    }
  }

  updateDropdown() {
    this.checkSelectAll();
    this.selectionChange.emit(this.selected);
    this._onChange(this.selected);
  }

  getDropdownDisplay(selectedOptions: DropdownOption[] | MheOption[] | undefined, placeholder: string, updateDropdownLabel?: boolean) {
    const selectedLength = selectedOptions?.length;

    if (!selectedLength || !updateDropdownLabel) {
      return placeholder;
    }

    if (selectedLength === 1) {
      return selectedOptions[0].viewValue;
    }

    return `${selectedOptions.length} ${this.selectedSuffix}`;
  }

  hasPlaceholderText(selectedOptions: DropdownOption[] | MheOption[] | undefined): boolean {
    return selectedOptions?.length === 0;
  }

  linkClickHandler(e: MouseEvent, option: MheOption) {
    if (!(option.href || option.routerLink) || option.disabled) {
      e.preventDefault();
    }
  }

  linkFocusHandler(event: any, option: MheOption) {
    const dropdownItem = event.target.parentElement;

    if (option.disabled) {
      return;
    }

    this.listItems.forEach((listItem) => {
      listItem.classList.remove('focused');
    });
    dropdownItem.classList.add('focused');
    this.focusedIndex = option.value;
  }

  toggleMenu(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.isExpanded = !this.isExpanded;

    if (this.isExpanded) {
      this.dropdownService.emitOpenEvent(this);
      this.showDropdown();
    } else {
      this.hideDropdown();
    }

    if (this.isExpanded && !this.multiple) {
      const initialSelection = this.getInitialSelectionLink();
      this.focusedOption = initialSelection;

      if (initialSelection) {
        setTimeout(() => {
          (initialSelection as any).focus();
          // Due to existing functionality where focus is immediately moved to the first menu item on open,
          // a 600ms delay is added to ensure screen readers announce the expanded state of the button
          // (e.g., "Create Test, button, expanded") before shifting focus.
          // Without this delay, screen readers like NVDA skip or cut off the expanded announcement and starts reading the first element.
        }, 600);
      }
    } else {
      this.focusControlButton();
    }
  }

  focusControlButton() {
    setTimeout(() => {
      if (this.hasIcon && this.toggleButtonWithIcon) {
        this.toggleButtonWithIcon.nativeElement.focus();
      } else if (this.splitActionText && this.splitActionButton) {
        this.splitActionButton.nativeElement.focus();
      } else if (this.controlButton) {
        this.controlButton.nativeElement.focus();
      }
    }, 0);
  }

  get dropdownButton() {
    const button = this.controlButton || this.toggleButtonWithIcon || this.splitActionButton;
    return button.nativeElement;
  }

  getInitialSelectionLink() {
    if (!this.listItems || this.listItems.length === 0) {
      return undefined;
    }

    let item = this.listItems[0];

    if (this.selected?.length) {
      const selectedValue = this.selected[0].value;

      this.listItems.forEach((listitem, index) => {
        const option = this._filteredOptions[index];

        if (option && option.value === selectedValue) {
          item = listitem;
        }
      });
    }

    return item.querySelector('.dropdown-item');
  }

  incrementSelectionLink() {
    let currIdx = this.listItems.findIndex((e, i) => e.classList.contains('focused'));
    currIdx = currIdx === this.listItems.length - 1 ? -1 : currIdx;
    let focusedOption;

    for (let i = currIdx + 1; i < this.listItems.length; i++) {
      const currentListItem = this.listItems[i];
      if (
        currentListItem.classList.contains('disabled') ||
        currentListItem.classList.contains('option-group-title')
      ) {
        continue;
      } else {
        focusedOption = this.listItems[i].querySelector('.dropdown-item');
        break;
      }
    }

    if (focusedOption) {
      this.focusedOption = focusedOption;
      this.focusedOption.focus();
    }
  }

  decrementSelectionLink() {
    let currIdx = this.listItems.findIndex((e, i) => e.classList.contains('focused'));
    currIdx = currIdx === 0 ? this.listItems.length : currIdx;
    let focusedOption;

    for (let i = currIdx - 1; i >= 0; i--) {
      const currentListItem = this.listItems[i];
      if (
        currentListItem.classList.contains('disabled') ||
        currentListItem.classList.contains('option-group-title')
      ) {
        continue;
      } else {
        focusedOption = currentListItem.querySelector('.dropdown-item');
        break;
      }
    }

    if (focusedOption) {
      this.focusedOption = focusedOption;
      this.focusedOption.focus();
    }
  }

  onKey(event: KeyboardEvent) {
    switch (event.key) {
      case 'Escape':
        this.hideDropdown(true);
        break;
      case 'Tab':
        event.preventDefault();
        this.hideDropdown(false);
        if (event.shiftKey) {
          this.tabToNextFocusable(true);
        } else {
          this.tabToNextFocusable();
        }
        break;
      case ' ':
      case 'Enter':
        if (this.isExpanded && !this.searchIsFocused) {
          this.focusedOption.click();
          event.preventDefault();
        }
        break;
      case 'ArrowDown':
        this.incrementSelectionLink();
        event.preventDefault();
        break;
      case 'ArrowUp':
        this.decrementSelectionLink();
        event.preventDefault();
        break;
    }
  }

  tabToNextFocusable(goToPreviousElement = false) {
    const focusableElems = getFocusableElements();
    const dropdownButtonIndex = focusableElems.findIndex((element) => element === this.dropdownButton);

    if (this.previousFocusedElement && goToPreviousElement) {
      (this.previousFocusedElement as HTMLElement).focus();
    } else if (dropdownButtonIndex !== -1) {
      let nextIndex = goToPreviousElement ? dropdownButtonIndex - 1 : dropdownButtonIndex + 1;

      if (nextIndex > focusableElems.length) {
        nextIndex = 0;
      }

      const nextFocusable = focusableElems[nextIndex];
      (nextFocusable as HTMLElement).focus();
    }
  }

  onClickedOutside(event: Event) {
    if (!this.overlayRef?.overlayElement?.contains(event?.target as Node)) {
      this.hideDropdown(false);

      if (!isElementFocusable(document.activeElement as HTMLElement)) {
        this.focusControlButton();
      }
    }
  }

  disabledProp(disabled: any) {
    return disabled ? '' : null;
  }

  getOptionLabel(option: MheOption): string {
    const optionValueList = [option.viewValue];

    if (option.viewDescription) {
      optionValueList.push(option.viewDescription);
    }

    for (const i in option.viewIcons) {
      if (optionValueList) {
        optionValueList.push(option.viewIcons[i].label);
      }
    }
    if (option?.opensNewTab && option?.actionType === 'page') {
      optionValueList.push('opens in a new tab');
    }
    return optionValueList.join(' ');
  }

  _onChange = (_: any) => {};

  _onTouch = () => {};

  registerOnChange(fn: (value: any) => any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => any): void {
    this._onTouch = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this._changeDetectorRef.markForCheck();
  }

  writeValue(val: DropdownOption[] | MheOption[]) {
    if (val !== this.selected) {
      const selectedChange = new SimpleChange(this.selected, val, true);
      this.selected = val;
      this.ngOnChanges({ selected: selectedChange });

      this._changeDetectorRef.markForCheck();
    }
  }

  onScroll(event: any) {
    if (event.target.scrollHeight - event.target.scrollTop < event.target.offsetHeight) {
      this.scrollBottomReached.emit(event);
    }
  }

  searchOptions(event: Event) {
    const { target } = event as HTMLElementEvent;
    this._searchTerm = target.value;

    if (this.searchable) {
      this.searchTerm.emit(this._searchTerm);
    } else {
      this._filteredOptions = this.filterOptions(this._searchTerm);
    }

    this.checkSelectAll();
  }

  trackById(index: any, item: DropdownOption | MheOption) {
    return item.value;
  }

  // currently, searchTerm filters on leaf nodes only
  private filterOptions(searchTerm: string): MheOption[] {
    const options = [...this._flatOptions];

    if (!searchTerm.trim().length) {
      return options;
    }

    return options.filter((option) => {
      if (option.children?.length) {
        return false;
      } else {
        return option.viewValue.toLowerCase().includes(searchTerm.trim().toLowerCase());
      }
    });
  }
}
